import { Component, OnInit, computed, signal } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SelectItem } from 'primeng/api';
import { tap } from 'rxjs';
import { BaseComponent } from '../../../../core/abstracts/baseComponent';
import { State } from '../../../../core/store';
import { loadEmployeeUsers, loadViewableCampuses } from '../../../../core/store/employee/employee.action';
import {
  dismissHrEmployeeChange,
  dismissHrEmployeeChangeSuccess,
  getFormApprovalCount,
  getHrEmployeeChangeCount,
  getHrEmployeeChangeQueue,
  getUploadDocApprovalCount
} from '../../../../core/store/queues/queues.action';
import { deepClone, getOptoLinkFromUrl } from '../../../../helpers/utils';
import { HrEmployeeChangeQueueModel } from '../../../../models/queueModels';
import { UserAutoCompleteItem } from '../../../../models/utilModels';

@Component({
  selector: 'app-hr-employee-changes',
  templateUrl: './hr-employee-changes.component.html',
  styleUrls: ['./hr-employee-changes.component.scss'],
  standalone: false
})
export class HrEmployeeChangesComponent extends BaseComponent implements OnInit {
  list: any;
  filter: any;

  loadingList = true;
  canManage: boolean;

  campusList: SelectItem[];
  campusId = 0;

  selectedUserEmployee: UserAutoCompleteItem;
  userEmployeeList: UserAutoCompleteItem[];
  userId = 0;

  changeType = '';
  changeTypeList: SelectItem[] = [
    { label: 'All', value: '' },
    { label: 'W4 Changes', value: 'W4 Changes' },
    { label: 'Direct Deposit', value: 'Direct Deposit' },
    { label: 'Address', value: 'Address' },
    { label: 'Disability Status', value: 'Disability Status' },
    { label: 'Benefits Enrollment', value: 'Benefits Enrollment' },
    { label: 'Uploaded Document', value: 'Uploaded Document' }
  ];

  formApprovalCount = 0;
  uploadDocApprovalCount = 0;
  hrEmployeeChangeCount = 0;

  totalRecords = signal<number>(0);
  dropdownOptions = computed(() => {
    var total = this.totalRecords();
    return [
      { label: '20', value: 20 },
      { label: '50', value: 50 },
      { label: '100', value: 100 },
      { label: '200', value: 200 },
      { label: '500', value: 500 },
      { label: 'All', value: total > 0 ? total : 20 }
    ];
  });

  constructor(
    private actions$: Actions,
    private store: Store<State>
  ) {
    super();
  }

  ngOnInit(): void {
    this.subs.push(
      this.store.select(s => s.queuesState.loadingHrEmployeeChangeQueue).subscribe(loading => {
        this.loadingList = loading;
      }),
      this.store.select(s => s.accountState.user?.permissionIDs).subscribe(permissionIDs => {
        this.canManage = permissionIDs && !!permissionIDs.find(p => p === 6100);
      }),
      this.store.select(s => s.employeeState.campuses).subscribe(campuses => {
        this.campusList = campuses && campuses.length > 0 ? campuses
          : [{ label: 'loading...', value: undefined }];
      }),
      this.store.select(s => s.employeeState.employeeUsers).subscribe(users => {
        this.userEmployeeList = users.map(x => ({
          id: x.value,
          name: x.label
        }));
      }),
      this.store.select(s => s.queuesState.hrEmployeeChangeQueue).subscribe(report => {
        this.list = report;
        if (report != null) {
          this.totalRecords.set(report.totalRecords);
        }
      }),
      this.store.select(s => s.queuesState.formApprovalCount).subscribe(count => {
        this.formApprovalCount = count;
      }),
      this.store.select(s => s.queuesState.uploadDocApprovalCount).subscribe(count => {
        this.uploadDocApprovalCount = count;
      }),
      this.store.select(s => s.queuesState.hrEmployeeChangeCount).subscribe(count => {
        this.hrEmployeeChangeCount = count;
      }),
      this.actions$.pipe(
        ofType(dismissHrEmployeeChangeSuccess),
        tap(action => {
          this.reloadList();
          this.store.dispatch(getHrEmployeeChangeCount());
        })
      ).subscribe()
    );

    this.store.dispatch(loadViewableCampuses());
    this.store.dispatch(getFormApprovalCount());
    this.store.dispatch(getUploadDocApprovalCount());
    this.store.dispatch(getHrEmployeeChangeCount());
  }

  reloadFilter(event) {
    this.filter = event;
    this.reloadList();
  }

  reloadList() {
    this.setFilter();

    this.store.dispatch(getHrEmployeeChangeQueue({
      filter: this.filter
    }));
  }

  getStaffList(event) {
    this.store.dispatch(loadEmployeeUsers({
      prefixText: event.query,
      programId: this.campusId,
      payrollClassificationId: null,
      showTerminated: false,
      showInactive: false
    }));
  }

  reloadListAfterStaffChange(selectedUser) {
    if (typeof selectedUser.value.id !== 'number') {
      return;
    }

    this.userId = selectedUser.value.id;
    this.reloadList();
  }

  reloadListAfterClear(event) {
    this.userId = 0;
    this.reloadList();
  }

  setFilter() {
    this.filter = deepClone(this.filter || {});
    this.filter.filters = this.filter.filters || {};
    this.filter.filters.userId = { Name: 'userId', Value: { value: this.userId.toString() } };
    this.filter.filters.programId = { Name: 'programId', Value: { value: this.campusId.toString() } };
    this.filter.filters.changeType = { Name: 'changeType', Value: { value: this.changeType } };
  }

  getTerminationString(list: HrEmployeeChangeQueueModel) {
    if (list.terminationDate != null) {
      return '(Separated)';
    }

    if (list.inactive) {
      return '(Inactive)';
    }

    return '';
  }

  dismissChange(model: HrEmployeeChangeQueueModel) {
    if (!model.id) {
      return;
    }

    this.store.dispatch(dismissHrEmployeeChange({ notificationId: model.id }));
  }

  editUser(model: HrEmployeeChangeQueueModel) {
    window.open(getOptoLinkFromUrl() + '/Administration/ManageUsers/Edit/' + model.userId);
  }
}
